import React, { Component, PureComponent } from "react";
import EditableTable from "@components/Table/EditableTable";
import {
  WorkorderColumns,
  WorkorderProps,
  WORKORDER_PRIORITY_THEME,
  WORKORDER_STATUS_THEME,
  WORKORDER_WRAPPER_STATUS_THEME,
} from "@containers/facility/Workorder/WorkorderColumns";
import _ from "lodash";
import actions from "@redux/workorder/actions";
import { connect, useSelector } from "react-redux";
import { api } from "@redux/api";
import { WorkorderFormFields } from "@containers/facility/Workorder/WorkorderFormFields";
import { WorkorderComment } from "@containers/facility/Workorder/WorkorderComment";
import { Button, Drawer, Form, Input, Tooltip } from "antd";
import moment from "moment/moment";
import PermissionControl from "@components/Permission/Permission";
import queryString from "query-string";
import { withRouter } from "@/router/withRouter";
import { hasValues } from "@components/help";
import WorkOrderChart from "./workOderChart";
import { BarChartOutlined, PlusSquareOutlined, MinusSquareOutlined } from "@ant-design/icons";
import { useKeepAliveContext } from "@/hooks/use-keep-alive-context";
import { useSmartNavigate } from "@/hooks/use-smart-navigate";
import { EventName, useEventBus } from "@/hooks/useEventBus";
import { UserSelect } from "./UserSelect";
import { useAuthEditable, useAuthFullAccess } from "@/hooks/use-auth";
import { buildFacilityPath, getFacilityContext } from "@/utils/route-context";
import DatePicker from "@components/ResponsiveDatePicker";
import Select from "@components/ResponsiveSelect";
import WorkorderNotification from "@containers/facility/Workorder/WorkorderNotification";
import "./Workorder.css";

const { Option } = Select;

const WORKORDER_STATUS_OPTIONS = [
  "Not Started",
  "InProgress",
  "On-Hold",
  "Verifying",
  "Completed",
];

const WORKORDER_ACTIVE_STATUSES = WORKORDER_STATUS_OPTIONS.filter(
  (status) => status !== "Completed"
);

const normalizeStatusValue = (status) => {
  if (Array.isArray(status)) return status;
  if (typeof status === "string") return status.split(",").filter(Boolean);
  return status;
};

const normalizeMultiValue = (value) => {
  if (Array.isArray(value)) return value.filter((item) => item != null && item !== "");
  if (typeof value === "string") return value.split(",").filter(Boolean);
  if (value == null || value === "") return [];
  return [value];
};

const appendQueryParam = (parts, key, value) => {
  if (Array.isArray(value)) {
    value
      .filter((item) => item != null && item !== "")
      .forEach((item) => appendQueryParam(parts, key, item));
    return;
  }
  if (value == null || value === "") return;
  parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
};

const WORKORDER_MOBILE_EXCLUDE_KEYS = [
  "equipment",
  "equipment_name",
  "status",
  "priority",
  "energy_impact",
  "Comments",
  "id",
  "reportDate_edit",
  "modified_date_hidden",
];

const WORKORDER_MOBILE_INCLUDE_KEYS = [
  "description",
  "responsible_party",
  "assignTo_name",
  "complete_date",
  "reportDate",
  "due_date",
  "recommended_action",
  "workordernumber",
  "project",
  "reportBy",
  "created_date",
  "createdby_name",
  "lastModified_name",
];

const formatMobileModifyDate = (value) => {
  if (value == null || value === "") return "-";
  const date = moment(value);
  return date.isValid() ? date.format("YYYY-MM-DD HH:mm") : "-";
};

const renderMobileMeta = (label, value, className = "", valueStyle = {}) => (
  <span className={`workorder-mobile-card-meta ${className}`.trim()}>
    <span className="workorder-mobile-card-meta-label">{label}: </span>
    <span className="workorder-mobile-card-meta-value" style={valueStyle}>
      {value || "-"}
    </span>
  </span>
);

const handleWorkorderDetailNavigation = (record, navigateToWorkorderDetail) => {
  if (!record || record._isWrapperRow) return;
  navigateToWorkorderDetail(record.id);
};

const renderWorkorderMobileHeader = (record, navigateToWorkorderDetail) => {
  const equipment = record._isWrapperRow
    ? (record._equipment_list || []).join(", ")
    : record.equipment_name;
  const issue = record._isWrapperRow ? record._wrapper_label : record.issue;
  const modifyDate = formatMobileModifyDate(record.modified_date);
  const statusTheme = record._isWrapperRow
    ? WORKORDER_WRAPPER_STATUS_THEME[record.status]
    : WORKORDER_STATUS_THEME[record.status];
  const priorityTheme = WORKORDER_PRIORITY_THEME[record.priority];
  const titleClassName = [
    "workorder-mobile-card-title",
    !record._isWrapperRow ? "workorder-mobile-card-title-link" : "",
  ].filter(Boolean).join(" ");
  const handleTitleClick = (event) => {
    if (record._isWrapperRow) return;
    event.stopPropagation();
    navigateToWorkorderDetail(record.id);
  };

  return (
    <div className="workorder-mobile-card">
      <div className="workorder-mobile-card-main">
        <span
          className={titleClassName}
          title={issue || ""}
          onClick={handleTitleClick}
        >
          {issue || "-"}
        </span>
      </div>

      <div className="workorder-mobile-card-details">
        <span className="workorder-mobile-card-equipment" title={equipment || ""}>
          {equipment || "-"}
        </span>

        <div className="workorder-mobile-card-middle">
          {renderMobileMeta(
            "Status",
            record.status,
            "workorder-mobile-card-meta-status",
            statusTheme ? { color: statusTheme.color } : {}
          )}
          {renderMobileMeta(
            "Priority",
            record.priority,
            "workorder-mobile-card-meta-priority",
            priorityTheme ? { color: priorityTheme.color } : {}
          )}
        </div>

        <div className="workorder-mobile-card-end">
          <div className="workorder-mobile-card-end-top">
            <span className="workorder-mobile-card-date">{modifyDate}</span>
            {!record._isWrapperRow ? (
              <span
                className="workorder-mobile-card-chat"
                onClick={(event) => event.stopPropagation()}
              >
                <WorkorderNotification record={record} />
              </span>
            ) : null}
          </div>
          {renderMobileMeta(
            "Energy Impact",
            record.energy_impact,
            "workorder-mobile-card-meta-energy"
          )}
        </div>
      </div>
    </div>
  );
};

/** 工单列表中合成 wrapper 父行时：用子工单聚合并与 WorkorderWrapper 时间对齐，供日期列展示。 */
function _minMomentInRows(rows, key) {
  const valid = rows
    .map((r) => r[key])
    .filter((v) => v != null && v !== "")
    .map((v) => moment(v))
    .filter((m) => m.isValid());
  if (!valid.length) return null;
  return moment.min(valid).toISOString();
}

function _maxMomentInRows(rows, key) {
  const valid = rows
    .map((r) => r[key])
    .filter((v) => v != null && v !== "")
    .map((v) => moment(v))
    .filter((m) => m.isValid());
  if (!valid.length) return null;
  return moment.max(valid).toISOString();
}

/** DateField 列（仅日期）用 YYYY-MM-DD，避免 UTC 日界线与 moment(undefined) 误显示为“今天”。 */
function _earliestYmd(rows, key) {
  const valid = rows
    .map((r) => r[key])
    .filter((v) => v != null && v !== "")
    .map((v) => moment(v))
    .filter((m) => m.isValid());
  if (!valid.length) return null;
  return moment.min(valid).format("YYYY-MM-DD");
}

function _mergeWrapperModified(first, workorders) {
  const childMax = _maxMomentInRows(workorders, "modified_date");
  const wAt = first.workorder_wrapper_modified_at;
  if (wAt) {
    const wM = moment(wAt);
    if (!wM.isValid()) return childMax;
    const cM = childMax ? moment(childMax) : null;
    if (cM && cM.isValid()) {
      return moment.max(wM, cM).toISOString();
    }
    return wM.toISOString();
  }
  return childMax;
}

class Workorder extends PureComponent {
  actionRef = React.createRef();
  constructor(props) {
    super(props);
    this.tableRef = React.createRef();

    this.filterFormRef = React.createRef();
    this.state = {
      historyVisible: false,
      columnsStateMap: {},
      data: [],
      status: WORKORDER_ACTIVE_STATUSES,
      energy: [],
      equipment: null,
      equipmentId: [],
      equipmentOptions: [],
      equipmentLookupLoading: false,
      project: null,
      project_name: null,
      equipment_name: null,
      priority: [],
      isLoading: false,
      query: null,
      completeYear: null,
      completeMonth: null,
      recordYear: null,
      recordMonth: null,
      groupedStatusData: [],
      groupedPriorityData: [],
      groupedMonthlyData: [],
      panel: null,
      complete_date: [null, null],
      due_date: [null, null],
      created_date: [null, null],
      report_date: [null, null],
      modify_date: [null, null],
      project_keyword: null,
      equipment_keyword: null,
      reportBy: null,
      tableRef: null,
      filterHasValue: false,
      assignTo: [],
      wo_id: null,
      responsible_party: "",
    };
    this.debouncedLoadEquipmentOptions = _.debounce(this.loadEquipmentOptions, 250);
  }

  componentDidMount() {
    const params = queryString.parse(this.props.location.search);
    if (params?.status) {
      this.setState({
        status: normalizeMultiValue(params.status),
      });
    }
    if (params?.priority) {
      this.setState({
        priority: normalizeMultiValue(params.priority),
      });
    }
    if (params?.complete_date) {
      this.setState({
        complete_date: [
          moment().subtract(params?.complete_date, "days"),
          moment(),
        ],
      });
    }
    if (params?.report_date) {
      this.setState({
        report_date: [moment().subtract(params?.report_date, "days"), moment()],
      });
    }
    if (params?.created_date) {
      this.setState({
        created_date: [
          moment().subtract(params?.created_date, "days"),
          moment(),
        ],
      });
    }
    if (params?.modify_date) {
      this.setState({
        modify_date: [moment().subtract(params?.modify_date, "days"), moment()],
      });
    }
    if (params?.id) {
      this.setState({
        wo_id: params.id,
      });
    }
    if (params?.equipmentId) {
      this.setState({
        equipmentId: normalizeMultiValue(params.equipmentId),
      });
    }
    // this.setupWebSocket();
  }

  componentDidUpdate(prevProps) {
    // Listen for refreshKey change to refresh table
    if (prevProps.refreshKey !== this.props.refreshKey && this.props.refreshKey > 0) {
      if (this.tableRef && this.tableRef.current) {
        this.tableRef.current.refresh();
      }
    }
  }

  getAllWorkOrders = (query) => {
    return api
      .allworkorderview(query)
      .then((response) => {
        let dataSource = response.data;
        this.setState({
          groupedMonthlyData: dataSource?.sorted_monthly_data,
        });
      })
      .catch((error) => error);
  };

  handleCreateWorkorder = (body) => {
    // console.log('created', this.props.parentProject)

    body["project"] = this.props.SelectedProject;

    let parent = this.props.relatedList;
    if (parent) {
      body["priority"] = "Urgent";
      body["project"] = this.props.parentProject;
      body["equipment"] = this.props.parentEquipment;
    }

    return api
      .createWorkorder(body)
      .then((response) => {
        // console.log('create', response);
        return response.data;
      })
      .catch((error) => error);
  };

  handleDeleteWorkorder = (id) => {
    return api
      .deleteWorkorder(id)
      .then((response) => {
        return response.data;
      })
      .catch((error) => error);
  };

  handleBulkInsert = (body) => {
    return api
      .bulkWorkorderInsert(body)
      .then((response) => {
        return response.data;
      })
      .catch((error) => {
        return error;
      });
  };

  handleBulkDelete = (body) => {
    const validRows = (body || []).filter((r) => r && !r._isWrapperRow);
    if (!validRows.length) {
      return Promise.resolve({ code: 200 });
    }
    return api
      .bulkDeleteWorkorder(validRows)
      .then((response) => {
        return response.data;
      })
      .catch((error) => error);
  };

  handOnlineSave = (key, row) => {
    if (String(key).startsWith("wrapper-") || row._isWrapperRow) {
      return Promise.resolve();
    }

    row["project"] = this.props.SelectedProject;

    let parent = this.props.relatedList;
    if (parent) {
      row["priority"] = "Urgent";
      row["project"] = this.props.parentProject;
    }
    row["modifiedby_name"] = this.props.userName;

    // 确保assignTo字段与assignTo_name对应
    if (row.assignTo_name && !row.assignTo) {
      // 如果只有名称没有ID，可能需要从其他地方获取ID
      console.log("assignTo_name存在但assignTo不存在，可能需要处理");
    }

    return api
      .editWorkorder(key, row)
      .then((response) => {
        // 如果表格引用存在，尝试更新表格数据
        if (this.state.tableRef && this.state.tableRef.current) {
          this.state.tableRef.current.refresh();
        }
        return response;
      })
      .catch((error) => error);
  };

  handleFilterHasValule = (e) => {
    const initialState = {
      equipmentId: normalizeMultiValue(this.state.equipmentId),
      project_name: this.state.project_name,
      status: this.state.status,
      priority: this.state.priority,
      energy: this.state.energy,
      reportBy: this.state.reportBy,
      complete_date: this.state.complete_date,
      due_date: this.state.due_date,
      report_date: this.state.report_date,
      created_date: this.state.created_date,
      modify_date: this.state.modify_date,
      responsible_party: this.state.responsible_party,
    };
    const filterHasValue = hasValues(initialState);
    return filterHasValue;
  };

  buildWorkorderQuery = (baseQuery = "", extraParams = {}) => {
    const cleanBaseQuery = String(baseQuery || "").replace(/^\?/, "").replace(/^&/, "");
    const parts = cleanBaseQuery ? [cleanBaseQuery] : [];

    Object.entries(extraParams).forEach(([key, value]) => {
      appendQueryParam(parts, key, value);
    });

    return parts.join("&");
  };

  /** 将 API 返回的扁平工单列表按 workorder_wrapper 分组，生成树形数据结构。
   *  有 wrapper 的 PM 工单合并为一行父级摘要行，展开后显示子工单。
   *  无 wrapper 的 CM/历史工单保持扁平行。
   */
  static buildGroupedData(flatList) {
    const grouped = {};
    const ungrouped = [];

    flatList.forEach((wo) => {
      if (wo.workorder_wrapper != null) {
        const wrapperId = wo.workorder_wrapper;
        if (!grouped[wrapperId]) {
          grouped[wrapperId] = [];
        }
        grouped[wrapperId].push(wo);
      } else {
        ungrouped.push(wo);
      }
    });

    const wrapperRows = Object.entries(grouped).map(([wrapperId, workorders]) => {
      const first = workorders[0];
      const equipmentList = [
        ...new Set(workorders.map((w) => w.equipment_name).filter(Boolean)),
      ];
      const dateLabel = first.wrapper_start_date || first.created_date || "";
      const dateFormatted = dateLabel
        ? dateLabel.slice(0, 10)
        : "";
      const typeLabel = first.workorder_type || "PM";
      // 列表 Issue 列左侧已固定显示「PM」徽标，_wrapper_label 不可再带「PM · …」，否则会出现 PM PM · 日期
      const planName = first.pm_plan_name || null;
      const _wrapperLabel =
        planName && dateFormatted
          ? `${planName} · ${dateFormatted}`
          : planName
            ? planName
            : dateFormatted || typeLabel;

      const lastIdx = workorders.length - 1;
      const children = workorders.map((w, idx) => ({
        ...w,
        _isLastChild: idx === lastIdx,
      }));

      const createdDate =
        first.workorder_wrapper_created_at ||
        _minMomentInRows(workorders, "created_date");
      const modifiedDate = _mergeWrapperModified(first, workorders);
      const reportYmd = _earliestYmd(workorders, "reportDate");
      const dueYmd = _earliestYmd(workorders, "due_date");
      const completeYmd = _earliestYmd(workorders, "complete_date");

      return {
        _isWrapperRow: true,
        id: `wrapper-${wrapperId}`,
        _wrapper_id: parseInt(wrapperId, 10),
        _wrapper_label: _wrapperLabel,
        _equipment_list: equipmentList,
        issue: planName
          ? (dateFormatted
            ? `PM · ${planName} · ${dateFormatted}`
            : `PM · ${planName}`)
          : `PM · ${dateFormatted || typeLabel}`,
        status: first.wrapper_status || "Open",
        equipment: null,
        equipment_name: null,
        pm_plan_name: first.pm_plan_name || null,
        wrapper_start_date: first.wrapper_start_date || null,
        created_date: createdDate,
        modified_date: modifiedDate,
        reportDate: reportYmd,
        due_date: dueYmd,
        complete_date: completeYmd,
        children,
      };
    });

    return [...wrapperRows, ...ungrouped];
  }

  handleFilter = (query) => {
    const filterHasValue = this.handleFilterHasValule();

    console.log(filterHasValue, "filterHasValue");
    this.setState({
      isLoading: true,
      filterHasValue,
    });

    let query_new = this.buildWorkorderQuery(query, {
      id: this.state.wo_id,
      assignTo: this.state.assignTo,
      equipmentId: this.state.equipmentId,
      project_name: this.state.project_name,
      status: normalizeMultiValue(this.state.status),
      priority: this.state.priority,
      reportBy: this.state.reportBy,
      energy_impact: this.state.energy,
      responsible_party: this.state.responsible_party,
      complete_date_start: this.state.complete_date[0]
        ? moment(this.state.complete_date[0]).format("YYYY-MM-DD")
        : null,
      complete_date_end: this.state.complete_date[1]
        ? moment(this.state.complete_date[1]).format("YYYY-MM-DD")
        : null,
      due_date_start: this.state.due_date[0]
        ? moment(this.state.due_date[0]).format("YYYY-MM-DD")
        : null,
      due_date_end: this.state.due_date[1]
        ? moment(this.state.due_date[1]).format("YYYY-MM-DD")
        : null,
      report_date_start: this.state.report_date[0]
        ? moment(this.state.report_date[0]).format("YYYY-MM-DD")
        : null,
      report_date_end: this.state.report_date[1]
        ? moment(this.state.report_date[1]).format("YYYY-MM-DD")
        : null,
      created_date_start: this.state.created_date[0]
        ? moment(this.state.created_date[0]).format("YYYY-MM-DD")
        : null,
      created_date_end: this.state.created_date[1]
        ? moment(this.state.created_date[1]).format("YYYY-MM-DD")
        : null,
      modify_date_start: this.state.modify_date[0]
        ? moment(this.state.modify_date[0]).format("YYYY-MM-DD")
        : null,
      modify_date_end: this.state.modify_date[1]
        ? moment(this.state.modify_date[1]).format("YYYY-MM-DD")
        : null,
    });
    let parent = this.props.relatedList;
    if (parent) {
      if (this.props.parentProject) {
        query_new = this.buildWorkorderQuery(query_new, { project: this.props.parentProject });
      }
      if (this.props.parentEquipment) {
        query_new = this.buildWorkorderQuery(query_new, { equipment: this.props.parentEquipment });
      }
    }
    return api
      .filterWorkorder(query_new)
      .then((response) => {
        const flatList = response.data.results || [];
        const groupedData = Workorder.buildGroupedData(flatList);
        return {
          data: groupedData,
          success: true,
          total: response.data.count,
        };
      })
      .catch((error) => error);
  };

  getWorkorder = (data) => {
    this.setState({
      data,
      isLoading: false,
    });
    // this.props.setWorkorderSuccess(data)
  };

  handleFormRelated = (e) => {
    console.log("handleFormRelated", e);
  };

  handleClearButtonClick = (e) => {
    this.setState(
      {
        status: null,
        priority: null,
        energy: [],
        equipment: null,
        project: null,
        query: null,
      },
      function () {
        this.actionRef.current.reset();
      }
    );
  };
  handlePostComment = (workorderId, equipmentId) => {
    if (workorderId) {
      return <WorkorderComment id={workorderId} equipmentId={equipmentId} />;
    }
  };

  handleEquipmentLookup = (e) => {
    return api
      .lookupEquipments(e)
      .then((response) => {
        let dataSource = response.data;
        return {
          data: dataSource,
          success: true,
          total: response.data.count,
        };
      })
      .catch((error) => {
        return error;
      });
  };

  loadEquipmentOptions = (keyword = "") => {
    this.setState({ equipmentLookupLoading: true });
    return api
      .lookupEquipments(encodeURIComponent(keyword || ""))
      .then((response) => {
        const options = (response.data || []).map((equipment) => ({
          value: equipment.id,
          label: equipment.name,
        }));
        this.setState({
          equipmentOptions: options,
          equipmentLookupLoading: false,
        });
        return options;
      })
      .catch((error) => {
        this.setState({ equipmentLookupLoading: false });
        return error;
      });
  };

  handleSearchSelect = (e, type) => {
    this.setState(
      {
        [type]: e,
        limit: null,
      },
      function () {
        this.actionRef.current.reset();
      }
    );
  };

  handleSearch = (e, type) => {
    this.setState(
      {
        [type]: e?.target ? e.target.value : e,
        limit: null,
      },
      function () {
        this.actionRef.current.reset();
      }
    );
  };

  onDateChange = (dates, state) => {
    this.setState(
      {
        [state]: dates ?? [],
      },
      function () {
        this.actionRef.current.reset();
      }
    );
  };

  setFilterFieldValue = (fieldName, value) => {
    if (this.filterFormRef.current) {
      this.filterFormRef.current.setFieldValue(fieldName, value);
    }
    this.handleSearchSelect(value, fieldName);
  };

  handleHideCompletedStatus = () => {
    this.setFilterFieldValue("status", WORKORDER_ACTIVE_STATUSES);
  };

  handleSelectAllStatus = () => {
    this.setFilterFieldValue("status", WORKORDER_STATUS_OPTIONS);
  };

  handleAssignToMe = () => {
    this.setFilterFieldValue("assignTo", [this.props.CurrentUserId]);
  };

  resetFilters = () => {
    this.setState(
      {
        equipmentId: [],
        equipment_name: "",
        project_name: "",
        reportBy: "",
        status: WORKORDER_ACTIVE_STATUSES,
        priority: [],
        energy: [],
        complete_date: [null, null],
        due_date: [null, null],
        report_date: [null, null],
        created_date: [null, null],
        modify_date: [null, null],
        assignTo: [],
        responsible_party: "",
      },
      function () {
        if (this.filterFormRef && this.filterFormRef.current) {
          this.filterFormRef.current.resetFields();
          this.filterFormRef.current.setFieldValue("status", WORKORDER_ACTIVE_STATUSES);
        }
        this.actionRef.current.reset();
      }
    );
  };

  renderFilter = () => {
    return null;
  };

  renderSearches = () => {
    return (
      <Form
        ref={this.filterFormRef}
        className={"filter-form"}
        initialValues={{
          status: Array.isArray(this.state.status)
            ? this.state.status
            : typeof this.state.status === "string"
            ? this.state.status?.split(",")
            : null,
          equipmentId: normalizeMultiValue(this.state.equipmentId),
          priority: normalizeMultiValue(this.state.priority),
          assignTo: normalizeMultiValue(this.state.assignTo),
          responsible_party: this.state.responsible_party,
        }}
                        autoComplete="off">
        <Form.Item label={"Equipment"} name="equipmentId">
          <Select
            mode="multiple"
            allowClear
            showSearch
            className={"min-multi-selector"}
            style={{ width: "100%" }}
            filterOption={false}
            options={this.state.equipmentOptions}
            loading={this.state.equipmentLookupLoading}
            placeholder={"Equipment"}
            value={normalizeMultiValue(this.state.equipmentId)}
            onFocus={() => this.loadEquipmentOptions("")}
            onSearch={(keyword) => this.debouncedLoadEquipmentOptions(keyword)}
            onChange={(equipmentId) =>
              this.handleSearchSelect(normalizeMultiValue(equipmentId), "equipmentId")
            }
          />
        </Form.Item>

        <Form.Item label={"Project"} name="project_name">
          <Input
            allowClear
            style={{ width: "100%" }}
            placeholder={"Project"}
            value={this.state.project_name}
            onChange={(e) => this.handleSearch(e, "project_name")}
          />
        </Form.Item>

        <Form.Item label={"Reported By"} name="reportBy">
          <Input
            allowClear
            style={{ width: "100%" }}
            placeholder={"Reported By"}
            value={this.state.reportBy}
            onChange={(e) => this.handleSearch(e, "reportBy")}
          />
        </Form.Item>

        <Form.Item label={"Status"} name="status">
          <Select
            mode="multiple"
            allowClear
            placeholder={"Status"}
            className={"min-multi-selector"}
            style={{ width: "100%" }}
            value={normalizeStatusValue(this.state.status)}
            onChange={(e) => this.handleSearchSelect(e, "status")}
            quickActions={[
              {
                key: "hide-completed",
                label: "Hide Completed",
                onClick: this.handleHideCompletedStatus,
              },
              {
                key: "select-all",
                label: "Select All",
                onClick: this.handleSelectAllStatus,
              },
            ]}
          >
            <Option value="Not Started">Not Started</Option>
            <Option value="InProgress">In Progress</Option>
            <Option value="On-Hold">On-hold</Option>
            <Option value="Verifying">Verifying</Option>
            <Option value="Completed">Completed</Option>
          </Select>
        </Form.Item>

        <Form.Item label={"Priority"} name="priority">
          <Select
            mode="multiple"
            allowClear
            placeholder={"Priority"}
            className={"min-multi-selector"}
            style={{ width: "100%" }}
            value={normalizeMultiValue(this.state.priority)}
            onChange={(e) => this.handleSearchSelect(e, "priority")}
          >
            <Option value="Urgent">Urgent</Option>
            <Option value="Important">Important</Option>
            <Option value="Low">Low</Option>
          </Select>
        </Form.Item>

        <Form.Item label={"Responsible Party"} name="responsible_party">
          <Input
            allowClear
            style={{ width: "100%" }}
            placeholder={"Responsible Party"}
            value={this.state.responsible_party}
            onChange={(e) => this.handleSearch(e, "responsible_party")}
          />
        </Form.Item>

        <Form.Item label={"Energy Impact"} name="energy">
          <Select
            mode="multiple"
            allowClear
            placeholder={"Energy Impact"}
            className={"min-multi-selector"}
            style={{ width: "100%" }}
            value={normalizeMultiValue(this.state.energy)}
            onChange={(e) => this.handleSearchSelect(e, "energy")}
          >
            <Option value="High">High</Option>
            <Option value="Medium">Medium</Option>
            <Option value="Low">Low</Option>
            <Option value="None">None</Option>
          </Select>
        </Form.Item>

        <Form.Item label={"Assign To"} name="assignTo">
          <UserSelect
            mode="multiple"
            className={"min-multi-selector"}
            style={{ width: "100%" }}
            value={normalizeMultiValue(this.state.assignTo)}
            onChange={(e) => {
              this.handleSearchSelect(e, "assignTo");
            }}
            quickActions={[
              {
                key: "assign-to-me",
                label: "Assign to me",
                disabled: !this.props.CurrentUserId,
                onClick: this.handleAssignToMe,
              },
            ]}
          />
        </Form.Item>

        <Form.Item label={"Complete Date"} name="complete_date_start">
          <DatePicker.RangePicker
            value={this.state.complete_date}
            onChange={(dates) => this.onDateChange(dates, "complete_date")}
          />
        </Form.Item>

        <Form.Item label={"Report Date"} name="report_date_start">
          <DatePicker.RangePicker
            value={this.state.report_date}
            onChange={(dates) => this.onDateChange(dates, "report_date")}
          />
        </Form.Item>

        <Form.Item label={"Due Date"} name="due_date">
          <DatePicker.RangePicker
            value={this.state.due_date}
            onChange={(dates) => this.onDateChange(dates, "due_date")}
          />
        </Form.Item>

        <Form.Item label={"Created Date"} name="created_date_start">
          <DatePicker.RangePicker
            value={this.state.created_date}
            onChange={(dates) => this.onDateChange(dates, "created_date")}
          />
        </Form.Item>
        <Form.Item label={"Modify Date"} name="modify_date">
          <DatePicker.RangePicker
            value={this.state.modify_date}
            onChange={(dates) => this.onDateChange(dates, "modify_date")}
          />
        </Form.Item>
      </Form>
    );
  };

  handlePanelChange = (e) => {
    console.log("handlePanelChange", e);
    this.setState(
      {
        panel: e[0],
      },
      function () {
        if (e[0]) {
          this.getAllWorkOrders();
        }
      }
    );
  };

  gettableRef = (tableRef) => {
    this.setState({
      tableRef,
    });
  };

  onNew = () => {
    this.props.navigate(this.props.createWorkorderPath);
  };

  renderButtons = () => {
    return (
      <PermissionControl allowedPermissions={"1:workorder"}>
        <Tooltip title="Workorder history">
          <Button
            type="default"
            shape="circle"
            className="table-page-header-icon-btn"
            icon={<BarChartOutlined />}
            onClick={this.showHistory}
            aria-label="Workorder history"
          />
        </Tooltip>
      </PermissionControl>
    );
  };

  showHistory = () => {
    this.getAllWorkOrders();
    this.setState({
      historyVisible: true,
    });
  };

  hideHistory = () => {
    this.setState({
      historyVisible: false,
    });
  };

  getActionRef = (actionRef) => {
    this.actionRef.current = actionRef.current;
  };

  render() {
    let parent = this.props.relatedList;

    const treeExpandable = {
      indentSize: 20,
      /**
       * 防止缩进 / 占位 span 进到「勾选列」：rc-table 用 (expandIconColumnIndex||0)，0 会与 checkbox 同级挤压。
       * antd Table 会在 nest + rowSelection 且本项 undefined 时自动设为 1，但 editable/首帧等路径下不可靠。
       * 显式传 2：有 rowSelection 时 antd Table 会做 expandIconColumnIndex -= 1，合并列中为下标 1（Issue），勾选列仅存 checkbox。
       */
      expandIconColumnIndex: 2,
      childrenColumnName: "children",
      // 只有 wrapper 父行才显示展开/收起图标
      rowExpandable: (record) => !!(record._isWrapperRow && record.children?.length),
      expandIcon: ({ expanded, onExpand, record, expandable: canExpand }) => {
        /* 占位与 selection 列同宽时别再占 margin-right，否则在 table-layout:fixed + 窄选择列时会挤压勾选框 */
        if (!canExpand)
          return <span style={{ display: "inline-block", width: 20, marginRight: 0, verticalAlign: "middle" }} />;
        return expanded ? (
          <MinusSquareOutlined
            onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
            style={{ color: "#1d4ed8", fontSize: 16, cursor: "pointer", marginRight: 4, verticalAlign: "middle" }}
          />
        ) : (
          <PlusSquareOutlined
            onClick={(e) => { e.stopPropagation(); onExpand(record, e); }}
            style={{ color: "#1d4ed8", fontSize: 16, cursor: "pointer", marginRight: 4, verticalAlign: "middle" }}
          />
        );
      },
    };

    return (
      <div className="workorder-page px-5">
        <EditableTable
          ref={this.tableRef}
          getActionRef={this.getActionRef}
          columns={WorkorderColumns()}
          data={this.state.data}
          columnProps={WorkorderProps}
          formFields={WorkorderFormFields(this.state.tableRef)}
          formRelated={this.handleFormRelated}
          postComment={this.handlePostComment}
          getRecord={this.getWorkorder}
          createRecords={this.handleCreateWorkorder}
          deleteRecord={this.handleDeleteWorkorder}
          bulkInsert={this.handleBulkInsert}
          bulkDelete={this.handleBulkDelete}
          handOnlineSave={this.handOnlineSave}
          handleFilter={this.handleFilter}
          renderButtons={this.renderButtons}
          resetFilters={this.resetFilters}
          filterHasValue={this.state.filterHasValue}
          relatedList={this.props.relatedList}
          relatedId={this.props.relatedId}
          relatedObject={this.props.relatedObject}
          hide_search={false}
          isRowClickable={false}
          isChat={true}
          isRelated={false}
          isAttachments={true}
          setFilters={this.setFilters}
          getFormRef={this.gettableRef}
          renderSearches={this.renderSearches}
          onDoubleClick={(record) => handleWorkorderDetailNavigation(record, this.props.navigateToWorkorderDetail)}
          new_text={'Create Work Order'}
          onNew={this.onNew}
          hid_new={!this.props.editable}
          hide_imports={!this.props.editable}
          canDelete={this.props.fullAccess}
          wrapperRowClassName="workorder-wrapper-row"
          tableClassName="workorder-tree-table"
          tableExpandable={treeExpandable}
          selectionColumnWidth={52}
          hideMobileTableExpand={true}
          mobileCardHeaderRender={(record) => renderWorkorderMobileHeader(record, this.props.navigateToWorkorderDetail)}
          mobileExpandedExcludeKeys={WORKORDER_MOBILE_EXCLUDE_KEYS}
          mobileExpandedIncludeKeys={WORKORDER_MOBILE_INCLUDE_KEYS}
          defaultDisplayColumns={{
            id: {
              show: false,
            },
            equipment_name: {
              show: false,
            },
            recommended_action: {
              show: false,
            },
            workordernumber: {
              show: false,
            },
            project_name: {
              show: false,
            },
            Project: {
              show: false,
            },
            reportBy: {
              show: false,
            },
            created_date: {
              show: false,
            },
            createdby_name: {
              show: false,
            },
            lastModified_name: {
              show: false,
            },
          }}
        />
        <Drawer
          open={this.state.historyVisible}
          onClose={this.hideHistory}
          title="Workorder History"
          height={400}
          placement="bottom"
          forceRender={true}
        >
          <WorkOrderChart
            height={250}
            data={this.state.groupedMonthlyData}
            type={"date"}
            value={"value"}
            seriesField={"status"}
            slider={true}
            isStack={true}
            isGroup={false}
            colors={{
              Completed: "green",
              Verifying: "blue",
              "On-Hold": "lightgrey",
              InProgress: "orange",
              "Not Started": "red",
            }}
            title="Workorder History"
          />
        </Drawer>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    Workorder: _.get(state.Workorder, "data"),
    SelectedEquipment: _.get(state.Equipment, "selectedEquipment.id"),
    SelectedProject: _.get(state.Project, "selectedProject.id"),
    CurrentUserId: _.get(state.Auth, "userId"),
    userName:
      state?.Auth?.user ||
      state?.Auth?.first_name + " " + state?.Auth?.last_name,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    setWorkorderRequest: (e) => dispatch(actions.WorkorderRequest(e)),
    setWorkorderSuccess: (e) => dispatch(actions.WorkorderSuccess(e)),
  };
};

const withHooks = (Component) => {
  const NewComponent = (props) => {
    const { closeTab } = useKeepAliveContext();
    const editable = useAuthEditable('workorder');
    const fullAccess = useAuthFullAccess('workorder');
    const { navigateToWorkorderDetail } = useSmartNavigate();
    const facilityContext = useSelector((state) => getFacilityContext(state));
    const workorderListPath = buildFacilityPath("workorder", facilityContext) || "/switch-view";
    const createWorkorderPath = buildFacilityPath("create-workorder", facilityContext) || "/switch-view";
    const [refreshKey, setRefreshKey] = React.useState(0);

    useEventBus(EventName.CLOSE_WORKORDER, () => {
      closeTab(workorderListPath);
    });

    useEventBus(EventName.REFRESH_WORKORDER_LIST, () => {
      setRefreshKey(prev => prev + 1);
    });

    return <Component {...props} editable={editable} fullAccess={fullAccess} refreshKey={refreshKey} createWorkorderPath={createWorkorderPath} navigateToWorkorderDetail={navigateToWorkorderDetail} />;
  };
  return NewComponent;
};

export default withHooks(
  withRouter(connect(mapStateToProps, mapDispatchToProps)(Workorder))
);
